import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import '../styles/pages/_modules.scss';
import { Utensils, Heart, Apple, Leaf, Sparkles, Monitor, Droplet, CheckCircle } from 'lucide-react';

// Import image using require to handle spaces in filename
const nutritionalWellnessImage = require('../assets/Nutritional Resources.png');

interface ModuleCard {
  id: string;
  title: string;
  category: 'nutritionalM1' | 'nutritionalM2' | 'nutritionalM3' | 'nutritionalM4' | 'nutritionalM5' | 'nutritionalM6';
  image: string;
  icon: React.ReactNode;
  featured?: boolean;
}

interface ModuleProgress {
  moduleId: string;
  currentStep: number;
  completed: boolean;
}

const NutritionalResourcesPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [progress, setProgress] = useState<ModuleProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      if (!user || !token) return;

      try {
        // Get progress for all modules
        const moduleIds = ['nutritionalM1', 'nutritionalM2', 'nutritionalM3', 'nutritionalM4', 'nutritionalM5', 'nutritionalM6'];
        const responses = await Promise.all(
          moduleIds.map(moduleId =>
            fetch(`${API_CONFIG.ENDPOINTS.MODULE_PROGRESS}/${moduleId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              credentials: 'include'
            })
          )
        );

        const results = await Promise.all(
          responses.map(async (response, index) => {
            if (response.ok) {
              const data = await response.json();
              return {
                moduleId: moduleIds[index],
                ...data.data
              };
            }
            return {
              moduleId: moduleIds[index],
              currentStep: 0,
              completed: false
            };
          })
        );

        setProgress(results);
      } catch (error) {

      } finally {
        setIsLoading(false);
      }
    };

    fetchProgress();
  }, [user, token]);

  const handleModuleClick = (moduleId: string) => {
    switch (moduleId) {
      case 'm1':
        navigate('/panel/learn/nutritional-module-1');
        break;
      case 'm2':
        navigate('/panel/learn/nutritional-module-2');
        break;
      case 'm3':
        navigate('/panel/learn/nutritional-module-3');
        break;
      case 'm4':
        navigate('/panel/learn/nutritional-module-4');
        break;
      case 'm5':
        navigate('/panel/learn/nutritional-module-5');
        break;
      case 'm6':
        navigate('/panel/learn/nutritional-module-6');
        break;
      default:
        console.log(`Module ${moduleId} navigation not yet implemented`);
    }
  };

  const modules: ModuleCard[] = [
    {
      id: 'm1',
      title: 'Understanding Nutrition & Your Body',
      category: 'nutritionalM1',
      image: nutritionalWellnessImage.default || nutritionalWellnessImage,
      icon: <Apple size={24} />,
    },
    {
      id: 'm2',
      title: 'Building a Balanced Filipino Plate',
      category: 'nutritionalM2',
      image: nutritionalWellnessImage.default || nutritionalWellnessImage,
      icon: <Utensils size={24} />,
    },
    {
      id: 'm3',
      title: 'Eat Smart – More of the Good, Less of the Risky',
      category: 'nutritionalM3',
      image: nutritionalWellnessImage.default || nutritionalWellnessImage,
      icon: <Droplet size={24} />,
    },
    {
      id: 'm4',
      title: 'Smart Food Choices for Daily Life',
      category: 'nutritionalM4',
      image: nutritionalWellnessImage.default || nutritionalWellnessImage,
      icon: <Monitor size={24} />,
    },
    {
      id: 'm5',
      title: 'Mindful Eating & Self-Awareness',
      category: 'nutritionalM5',
      image: nutritionalWellnessImage.default || nutritionalWellnessImage,
      icon: <Heart size={24} />,
    },
    {
      id: 'm6',
      title: 'Nutrition for Long-Term Health',
      category: 'nutritionalM6',
      image: nutritionalWellnessImage.default || nutritionalWellnessImage,
      icon: <Leaf size={24} />,
    }
  ];

  const allRegularModules = modules.filter(module => !module.featured);

  if (isLoading) {
    return (
      <div className={`resources-page ${isDarkMode ? 'dark' : ''}`} style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  return (
    <div className={`resources-page ${isDarkMode ? 'dark' : ''}`}>
      <div className="module-resources_container">
        {/* Header Section */}
        <div className="module-resources_header">
          <div className="module-resources_header-content">
            <div className="module-resources_header-text">
              <h2>Nutritional Resources</h2>
            </div>
          </div>
        </div>

        {/* Modules Grid */}
        <div className="module-resources_modules">
          <div className="module-resources_modules-grid">
            {allRegularModules.length > 0 ? 
              allRegularModules.map((module: ModuleCard) => {
                const moduleProgress = progress.find(p => p.moduleId === module.category);
                const isCompleted = moduleProgress?.completed || false;
                return (
                  <div 
                    key={module.id} 
                    className={`module-resources_module-card ${isCompleted ? 'completed' : ''}`}
                    onClick={() => handleModuleClick(module.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleModuleClick(module.id);
                      }
                    }}
                  >
                    <div className="module-resources_module-image">
                      <img src={module.image} alt={module.title} />
                      {isCompleted && (
                        <div className="completion-overlay">
                          <CheckCircle 
                            className="check-icon"
                            strokeWidth={1.5}
                            absoluteStrokeWidth
                          />
                        </div>
                      )}
                    </div>
                    <div className="module-resources_module-content">
                      <h3 className="module-resources_module-title">{module.title}</h3>
                    </div>
                  </div>
                );
              })
              : <p>No modules found.</p>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalResourcesPage;

